import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from './hooks/useAuth'
import ErrorBoundary from './components/ErrorBoundary'

import LoginPage from './pages/LoginPage'
import SignupPage from './pages/user/SignupPage'
import ForgotPasswordPage from './pages/user/ForgotPasswordPage'
import VerifyEmailPage from './pages/user/VerifyEmailPage'

import UserLayout from './components/UserLayout'
import CertificationsPage from './pages/user/CertificationsPage'
import CertificationDetailPage from './pages/user/CertificationDetailPage'
import AddCertificationPage from './pages/user/AddCertificationPage'
import CEsPage from './pages/user/CEsPage'
import CEDetailPage from './pages/user/CEDetailPage'
import AddCEPage from './pages/user/AddCEPage'
import CEProgressPage from './pages/user/CEProgressPage'
import ProfessionsPage from './pages/user/ProfessionsPage'
import NotificationsPage from './pages/user/NotificationsPage'
import ProfilePage from './pages/user/ProfilePage'
import ChangePasswordPage from './pages/user/ChangePasswordPage'
import NotificationSettingsPage from './pages/user/NotificationSettingsPage'
import EditCertificationPage from './pages/user/EditCertificationPage'
import DeleteAccountPage from './pages/user/DeleteAccountPage'

import Layout from './components/Layout'
import DashboardPage from './pages/DashboardPage'
import UsersPage from './pages/UsersPage'
import ReportsPage from './pages/ReportsPage'
import EmailPage from './pages/EmailPage'
import LicensesPage from './pages/LicensesPage'
import CEManagementPage from './pages/CEManagementPage'
import SettingsPage from './pages/SettingsPage'
import UserProfilePage from './pages/UserProfilePage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, error } = useAuth()
  
  console.log('[ProtectedRoute] isLoading:', isLoading, 'user:', user?.email, 'error:', error)
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }
  
  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to login')
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

function EmailVerificationCheck({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  
  if (user && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />
  }
  
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/" replace />
  }
  
  return <>{children}</>
}

function LegacyCERedirect() {
  const [searchParams] = useSearchParams()
  const ceId = searchParams.get('ceId')
  return <Navigate to={ceId ? `/ce/${ceId}` : '/ce'} replace />
}

function LegacyCertRedirect() {
  const [searchParams] = useSearchParams()
  const certId = searchParams.get('certificationId') || searchParams.get('id')
  return <Navigate to={certId ? `/certifications/${certId}` : '/'} replace />
}

function UserRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <EmailVerificationCheck>
        <UserLayout>
          {children}
        </UserLayout>
      </EmailVerificationCheck>
    </ProtectedRoute>
  )
}

function HomeRedirect() {
  const { user, isLoading } = useAuth()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }
  
  if (user?.role === 'admin' || user?.role === 'super_admin') {
    return <Navigate to="/admin" replace />
  }
  
  return (
    <UserRoute>
      <CertificationsPage />
    </UserRoute>
  )
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      
      {/* Email verification */}
      <Route 
        path="/verify-email" 
        element={
          <ProtectedRoute>
            <VerifyEmailPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Home - admins go to admin dashboard, users go to certifications */}
      <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
      <Route path="/app" element={<UserRoute><CertificationsPage /></UserRoute>} />
      <Route path="/certifications/new" element={<UserRoute><AddCertificationPage /></UserRoute>} />
      <Route path="/certifications/:id" element={<UserRoute><CertificationDetailPage /></UserRoute>} />
      <Route path="/certifications/:id/edit" element={<UserRoute><EditCertificationPage /></UserRoute>} />
      
      {/* User routes - CE */}
      <Route path="/ce" element={<UserRoute><CEsPage /></UserRoute>} />
      <Route path="/ce/new" element={<UserRoute><AddCEPage /></UserRoute>} />
      <Route path="/ce/progress/:id" element={<UserRoute><CEProgressPage /></UserRoute>} />
      <Route path="/ce/:id" element={<UserRoute><CEDetailPage /></UserRoute>} />
      
      {/* User routes - Professions */}
      <Route path="/professions" element={<UserRoute><ProfessionsPage /></UserRoute>} />
      
      {/* User routes - Notifications */}
      <Route path="/notifications" element={<UserRoute><NotificationsPage /></UserRoute>} />
      
      {/* User routes - Profile */}
      <Route path="/profile" element={<UserRoute><ProfilePage /></UserRoute>} />
      <Route path="/profile/password" element={<UserRoute><ChangePasswordPage /></UserRoute>} />
      <Route path="/profile/notifications" element={<UserRoute><NotificationSettingsPage /></UserRoute>} />
      <Route path="/profile/delete" element={<UserRoute><DeleteAccountPage /></UserRoute>} />
      
      {/* Admin routes */}
      <Route
        path="/admin"
        element={<AdminRoute><Layout><DashboardPage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/users"
        element={<AdminRoute><Layout><UsersPage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/users/:userId"
        element={<AdminRoute><Layout><UserProfilePage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/reports"
        element={<AdminRoute><Layout><ReportsPage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/email"
        element={<AdminRoute><Layout><EmailPage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/licenses"
        element={<AdminRoute><Layout><LicensesPage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/ce"
        element={<AdminRoute><Layout><CEManagementPage /></Layout></AdminRoute>}
      />
      <Route
        path="/admin/settings"
        element={<AdminRoute><Layout><SettingsPage /></Layout></AdminRoute>}
      />
      
      {/* Legacy Expo URL redirects */}
      <Route path="/CEDetail" element={<LegacyCERedirect />} />
      <Route path="/CertificationDetail" element={<LegacyCertRedirect />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
